import { useMemo } from "react";

import {
  ListOrchestrationWorkflowsQuery,
  useListOrchestrationWorkflowOptionsQuery,
  useListOrchestrationWorkflowsQuery,
} from "@/apollo/types";
import {
  isRunInFlight,
  scheduledRunAt,
  useOrchestrationPolling,
} from "@/hooks/useOrchestrationPolling";

type Workflow = ListOrchestrationWorkflowsQuery["orchestrationWorkflows"][0];

// Numeric, so the "1. …"/"2. …" prefixes people use to order their orchestrations sort as numbers.
const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name, undefined, { numeric: true });

export const hasRunInFlight = (workflows: Workflow[]) =>
  workflows.some((workflow) => isRunInFlight(workflow.currentRun?.status));

export const earliestNextRunAt = (workflows: Workflow[]) => {
  const earliest = Math.min(
    ...workflows.map((workflow) => scheduledRunAt(workflow) ?? Infinity),
  );
  return Number.isFinite(earliest) ? earliest : null;
};

export const useListOrchestrationWorkflows = () => {
  const { data, loading, startPolling, stopPolling } =
    useListOrchestrationWorkflowsQuery({
      fetchPolicy: "cache-and-network",
      skipPollAttempt: () => document.hidden,
    });

  const workflows = useMemo(
    () => [...(data?.orchestrationWorkflows ?? [])].sort(byName),
    [data?.orchestrationWorkflows],
  );

  useOrchestrationPolling({
    isRunning: data == null || hasRunInFlight(workflows),
    nextRunAt: earliestNextRunAt(workflows),
    startPolling,
    stopPolling,
  });

  return useMemo(() => {
    return {
      workflows,
      loading,
    };
  }, [workflows, loading]);
};

/**
 * For pickers that only name the workflows. The full list query carries every job of the current
 * and next run, which a dropdown has no use for.
 *
 * Mount one observer per screen: cache-and-network fetches once for each, so a section that
 * gates on the same list must let the selecter own it rather than querying alongside it.
 */
export const useOrchestrationWorkflowOptions = () => {
  const { data, loading } = useListOrchestrationWorkflowOptionsQuery({
    fetchPolicy: "cache-and-network",
  });

  const workflows = useMemo(
    () => [...(data?.orchestrationWorkflows ?? [])].sort(byName),
    [data?.orchestrationWorkflows],
  );

  return useMemo(() => {
    return {
      workflows,
      loading,
    };
  }, [workflows, loading]);
};
